'use client'

import { useState } from 'react'
import {
  Button,
  Column,
  ContentSwitcher,
  Grid,
  Layer,
  Switch,
  Tag,
  Tile,
} from '@carbon/react'
import { Checkmark, Launch } from '@carbon/react/icons'
import type { CatalogItem } from '@/lib/catalog'
import { catalogStatusLabel, isCatalogUnavailable } from '@/lib/catalog'
import { getCatalogStatus } from '@/lib/catalog-availability'
import { getVisiblePricingNote } from '@/lib/pricing'
import { catalogUiCopy } from '@/lib/catalog-ui-i18n'
import { BrevoChatButton } from '@/components/brevo-chat'

export function CatalogPricingSections({ item, locale = 'en' }: { item: CatalogItem; locale?: string }) {
  const [billingTermByGroup, setBillingTermByGroup] = useState<Record<string, number>>({})
  const copy = catalogUiCopy(locale)

  if (isCatalogUnavailable(getCatalogStatus(item))) return null

  return item.pricing?.map((group, groupIndex) => {
    const note = getVisiblePricingNote(group)

    return (
      <section
        key={group.title}
        id={groupIndex === 0 ? 'pricing' : undefined}
        className="section catalog-pricing"
        aria-labelledby={`${item.slug}-${group.title.replaceAll(' ', '-').toLowerCase()}`}
      >
        <Grid>
          <Column sm={4} md={8} lg={12}>
            <div className="price-group__intro">
              <div>
                <p className="section-label">{group.eyebrow}</p>
                <h2 id={`${item.slug}-${group.title.replaceAll(' ', '-').toLowerCase()}`} className="section__title">{group.title}</h2>
              </div>
              {isCatalogUnavailable(group.status) ? (
                <Tag type={group.status === 'out-of-stock' ? 'red' : 'blue'}>{catalogStatusLabel(group.status)}</Tag>
              ) : group.externalUrl ? (
                <Button data-carbon-action="Open service portal" href={group.externalUrl} target="_blank" rel="noreferrer" kind="tertiary" renderIcon={Launch}>{copy.openServicePortal}</Button>
              ) : null}
            </div>
            {note ? <p className="pricing-note">{note}</p> : null}
            {group.billingTerms ? (
              <ContentSwitcher
                className="pricing-term-switcher"
                selectedIndex={billingTermByGroup[group.title] ?? 0}
                onChange={({ name }) => {
                  const selectedIndex = group.billingTerms?.findIndex((term) => term.label === name) ?? -1
                  if (selectedIndex >= 0) {
                    setBillingTermByGroup((current) => ({ ...current, [group.title]: selectedIndex }))
                  }
                }}
                aria-label={`${group.title} billing term`}
              >
                {group.billingTerms.map((term) => (
                  <Switch key={term.label} name={term.label} text={`${term.label} · ${term.discountPercent}% off`} />
                ))}
              </ContentSwitcher>
            ) : null}
            {isCatalogUnavailable(group.status) ? (
              <BrevoChatButton label={copy.talkSupport} kind="tertiary" />
            ) : (
              <Layer>
                <div className="pricing-grid catalog-pricing-grid">
                  {group.plans.map((plan) => {
                    const selectedTerm = group.billingTerms?.[billingTermByGroup[group.title] ?? 0]
                    const discountedMonthlyPrice = selectedTerm && plan.priceValue !== undefined
                      ? plan.priceValue * (1 - selectedTerm.discountPercent / 100)
                      : undefined
                    const billedTotal = selectedTerm && discountedMonthlyPrice !== undefined
                      ? discountedMonthlyPrice * selectedTerm.months
                      : undefined

                    return (
                      <Tile key={plan.name} className="pricing-card">
                        <h3>{plan.name}</h3>
                        <strong>{discountedMonthlyPrice !== undefined ? `$${discountedMonthlyPrice.toFixed(2)}` : plan.price}<small>{plan.cadence}</small></strong>
                        {selectedTerm && billedTotal !== undefined ? (
                          <span className="pricing-card__billing">{copy.billedFor} ${billedTotal.toFixed(2)} · {selectedTerm.label} · {copy.save} {selectedTerm.discountPercent}%</span>
                        ) : null}
                        <p>{plan.description}</p>
                        <ul className="pricing-card__features">
                          {plan.features.map((feature) => (
                            <li key={feature}><Checkmark size={16} aria-hidden="true" />{feature}</li>
                          ))}
                        </ul>
                      </Tile>
                    )
                  })}
                </div>
              </Layer>
            )}
          </Column>
        </Grid>
      </section>
    )
  }) ?? null
}
